import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { useTranslation } from '../hooks/useTranslation';
import { Language, languageNames } from '../utils/i18n';
import { useMusic } from '../hooks/useMusic';

type SettingsView = 'main' | 'language' | 'music';

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const language = useGameStore((state) => state.language);
  const setLanguage = useGameStore((state) => state.setLanguage);
  const musicEnabled = useGameStore((state) => state.musicEnabled);
  const musicVolume = useGameStore((state) => state.musicVolume);
  const setMusicEnabled = useGameStore((state) => state.setMusicEnabled);
  const setMusicVolume = useGameStore((state) => state.setMusicVolume);
  const t = useTranslation();

  const [currentView, setCurrentView] = React.useState<SettingsView>('main');
  const [isLandscape, setIsLandscape] = React.useState(window.innerWidth > window.innerHeight);

  // Фоновая музыка для настроек (использует настройки из store)
  useMusic({
    type: 'menu',
    volume: musicVolume,
    enabled: musicEnabled
  });

  React.useEffect(() => {
    const handleResize = () => setIsLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const languages: Language[] = ['ru', 'en', 'es', 'fr', 'de', 'zh'];

  const containerStyle: React.CSSProperties = {
    minHeight: 'calc(100vh - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0))',
    width: 'calc(100vw - env(safe-area-inset-left, 0) - env(safe-area-inset-right, 0))',
    paddingTop: 'calc(2rem + env(safe-area-inset-top, 0))',
    paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0))',
    paddingLeft: 'calc(2rem + env(safe-area-inset-left, 0))',
    paddingRight: 'calc(2rem + env(safe-area-inset-right, 0))',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000',
    backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000 100%)',
    position: 'relative',
    overflow: 'hidden'
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: '#ca8a04',
    color: '#000',
    padding: isLandscape ? '1rem 2rem' : '1.25rem 2.5rem',
    fontSize: isLandscape ? '0.8rem' : '1rem',
    fontWeight: 'bold',
    fontFamily: '"Press Start 2P", cursive, sans-serif',
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    boxShadow: '0 6px 0 #854d0e',
    display: 'block',
    width: '100%',
    maxWidth: isLandscape ? '280px' : '320px',
    margin: '0 auto 1rem auto',
    transition: 'none',
  };

  const menuButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#1a1a1a',
    color: '#ca8a04',
    border: '2px solid #ca8a04',
    boxShadow: '0 4px 0 #854d0e',
    textTransform: 'none',
    fontSize: isLandscape ? '0.7rem' : '0.85rem',
    width: '100%',
    margin: '0 auto',
  };

  const languageButtonStyle: React.CSSProperties = {
    ...menuButtonStyle,
    marginBottom: '0.5rem',
    width: '100%',
    margin: '0 0 0.5rem 0',
  };

  const selectedLanguageButtonStyle: React.CSSProperties = {
    ...languageButtonStyle,
    backgroundColor: '#ca8a04',
    color: '#000',
    boxShadow: '0 4px 0 #854d0e, 0 0 15px rgba(202, 138, 4, 0.5)',
  };

  const toggleButtonStyle: React.CSSProperties = {
    ...menuButtonStyle,
    marginBottom: '1rem',
  };

  const volumeSliderStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: isLandscape ? '280px' : '320px',
    marginBottom: '1rem',
    appearance: 'none',
    WebkitAppearance: 'none',
    height: '8px',
    borderRadius: '4px',
    background: '#1a1a1a',
    outline: 'none',
    border: '2px solid #ca8a04',
  };

  const volumeLabelStyle: React.CSSProperties = {
    color: '#ca8a04',
    fontSize: isLandscape ? '0.6rem' : '0.7rem',
    marginBottom: '0.5rem',
    fontFamily: '"Press Start 2P", cursive, sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setMusicVolume(newVolume);
  };

  const renderMainView = () => (
    <>
      <h1 style={{
        color: '#ca8a04',
        fontSize: isLandscape ? '1.5rem' : '2rem',
        marginBottom: '2rem',
        fontFamily: '"Press Start 2P", cursive, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '0.1em'
      }}>
        {t.settings.title}
      </h1>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setCurrentView('language')}
        style={menuButtonStyle}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'translateY(2px)';
          e.currentTarget.style.boxShadow = '0 2px 0 #854d0e';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 0 #854d0e';
        }}
      >
        {t.settings.language} →
      </motion.button>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setCurrentView('music')}
        style={menuButtonStyle}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'translateY(2px)';
          e.currentTarget.style.boxShadow = '0 2px 0 #854d0e';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 0 #854d0e';
        }}
      >
        {t.settings.music} →
      </motion.button>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate('/')}
        style={buttonStyle}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'translateY(3px)';
          e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 6px 0 #854d0e';
        }}
      >
        {t.common.back}
      </motion.button>
    </>
  );

  const renderLanguageView = () => (
    <>
      <h1 style={{
        color: '#ca8a04',
        fontSize: isLandscape ? '1.2rem' : '1.5rem',
        marginBottom: '1rem',
        fontFamily: '"Press Start 2P", cursive, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '0.1em'
      }}>
        {t.settings.language}
      </h1>

      <div style={{
        color: '#ca8a04',
        fontSize: isLandscape ? '0.6rem' : '0.7rem',
        marginBottom: '1rem',
        fontFamily: '"Press Start 2P", cursive, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        opacity: 0.8
      }}>
        {t.settings.selectLanguage}: <span style={{ color: '#fff', opacity: 1 }}>{languageNames[language]}</span>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        width: '100%',
        maxWidth: isLandscape ? '280px' : '320px',
        marginBottom: '1rem',
        marginLeft: 'auto',
        marginRight: 'auto',
        alignItems: 'stretch',
        maxHeight: isLandscape ? '40vh' : '50vh',
        overflowY: 'auto',
        paddingRight: '5px'
      }}>
        {languages.map((lang) => (
          <motion.button
            key={lang}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setLanguage(lang)}
            style={language === lang ? selectedLanguageButtonStyle : languageButtonStyle}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(2px)';
              e.currentTarget.style.boxShadow = language === lang
                ? '0 2px 0 #854d0e, 0 0 15px rgba(202, 138, 4, 0.5)'
                : '0 2px 0 #854d0e';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = language === lang
                ? '0 4px 0 #854d0e, 0 0 15px rgba(202, 138, 4, 0.5)'
                : '0 4px 0 #854d0e';
            }}
          >
            {language === lang ? '✓ ' : ''}{languageNames[lang]}
          </motion.button>
        ))}
      </div>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setCurrentView('main')}
        style={buttonStyle}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'translateY(3px)';
          e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 6px 0 #854d0e';
        }}
      >
        {t.common.back}
      </motion.button>
    </>
  );

  const renderMusicView = () => (
    <>
      <h1 style={{
        color: '#ca8a04',
        fontSize: isLandscape ? '1.2rem' : '1.5rem',
        marginBottom: '1rem',
        fontFamily: '"Press Start 2P", cursive, sans-serif',
        textTransform: 'uppercase',
        letterSpacing: '0.1em'
      }}>
        {t.settings.music}
      </h1>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setMusicEnabled(!musicEnabled)}
        style={toggleButtonStyle}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'translateY(2px)';
          e.currentTarget.style.boxShadow = '0 2px 0 #854d0e';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 0 #854d0e';
        }}
      >
        {musicEnabled ? t.settings.disableMusic : t.settings.enableMusic}
      </motion.button>

      {musicEnabled && (
        <>
          <div style={volumeLabelStyle}>
            {t.settings.volume}: {Math.round(musicVolume * 100)}%
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={musicVolume}
            onChange={handleVolumeChange}
            style={volumeSliderStyle}
          />
        </>
      )}

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setCurrentView('main')}
        style={buttonStyle}
        onMouseDown={(e) => {
          e.currentTarget.style.transform = 'translateY(3px)';
          e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 6px 0 #854d0e';
        }}
      >
        {t.common.back}
      </motion.button>
    </>
  );

  return (
    <div style={containerStyle}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        style={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          width: '100%',
          maxWidth: isLandscape ? '600px' : '400px',
        }}
      >
        <AnimatePresence mode="wait">
          {currentView === 'main' && (
            <motion.div
              key="main"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {renderMainView()}
            </motion.div>
          )}
          {currentView === 'language' && (
            <motion.div
              key="language"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {renderLanguageView()}
            </motion.div>
          )}
          {currentView === 'music' && (
            <motion.div
              key="music"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {renderMusicView()}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};
